import { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { 
  LayoutDashboard, 
  Users, 
  Wrench, 
  Package, 
  FileText, 
  Star,
  LogOut,
  Settings,
  Shield,
  Menu,
  X,
  Inbox,
  ShoppingCart,
  CalendarDays,
  Truck,
  Store,
  ClipboardList,
  User,
  Smartphone
} from "lucide-react";
import axios from "axios";
import NotificationBell from "./NotificationBell";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Badge component
const Badge = ({ count, isNew }) => {
  if (!count || count === 0) return null;
  return (
    <span className={`ml-auto min-w-[20px] h-5 px-1.5 rounded-full text-xs font-bold flex items-center justify-center ${
      isNew ? "bg-red-500 text-white animate-pulse" : "bg-primary/20 text-primary"
    }`}>
      {count > 99 ? "99+" : count}
    </span>
  );
};

export const Sidebar = ({ isOpen, onClose }) => {
  const { user, logout, isAdmin, hasPermission } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ active_repairs: 0, new_repairs: 0, pending_intakes: 0, new_intakes: 0, repairs: 0, parts_to_order: 0, unpaid_invoices: 0, appointments_today: 0, active_deliveries: 0, unread_reviews: 0, pending_quotes: 0, warranty_alerts: 0 });
  const [seenPaths, setSeenPaths] = useState(() => {
    try { return JSON.parse(localStorage.getItem("seen_paths") || "{}"); } catch { return {}; }
  });
  const markSeen = (path) => {
    const updated = { ...seenPaths, [path]: stats[badgeKeyForPath(path)] || 0 };
    setSeenPaths(updated);
    localStorage.setItem("seen_paths", JSON.stringify(updated));
  };
  const badgeKeyForPath = (path) => {
    const map = {
      "/repairs": "repairs", "/admin/parts-to-order": "parts_to_order",
      "/invoices": "unpaid_invoices", "/admin/appointments": "appointments_today",
      "/delivery": "active_deliveries", "/admin/reviews": "unread_reviews",
      "/quotes": "pending_quotes", "/intake": "pending_intakes",
    };
    return map[path] || "";
  };
  const getBadge = (path, key) => {
    const val = stats[key] || 0;
    const seen = seenPaths[path] || 0;
    return val > seen ? val : 0;
  };

  useEffect(() => {
    fetchStats();
    const interval = setInterval(fetchStats, 60000); // refresh toutes les 60s
    return () => clearInterval(interval);
  }, []);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;
      const res = await axios.get(`${API_URL}/api/sidebar/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(res.data);
    } catch (err) {
      // Silencieux
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
    if (onClose) onClose();
  };

  const handleNavClick = (path) => {
    markSeen(path);
    if (onClose) onClose();
  };

  const navItems = [];
  
  if (hasPermission("dashboard")) {
    navItems.push({ to: "/", icon: LayoutDashboard, label: "Tableau de bord" });
  }
  if (hasPermission("repairs")) {
    navItems.push({ 
      to: "/repairs", icon: Wrench, label: "Réparations",
      badge: stats.active_repairs,
      badgeNew: stats.new_repairs > 0
    });
  }
  if (hasPermission("clients")) {
    navItems.push({ to: "/clients", icon: Users, label: "Clients" });
    navItems.push({ to: "/flotte", icon: Smartphone, label: "Flotte", badge: stats.warranty_alerts });
  }
  if (hasPermission("inventory")) {
    navItems.push({ to: "/inventory", icon: Package, label: "Stock" });
    navItems.push({ to: "/admin/parts-to-order", icon: ShoppingCart, label: "Pièces à commander", badge: getBadge("/admin/parts-to-order", "parts_to_order") });
  }
  if (hasPermission("invoices")) {
    navItems.push({ to: "/invoices", icon: FileText, label: "Factures", badge: getBadge("/invoices", "unpaid_invoices") });
  }
  if (hasPermission("reviews")) {
    navItems.push({ to: "/admin/reviews", icon: Star, label: "Avis", badge: getBadge("/admin/reviews", "unread_reviews") });
  }
  if (hasPermission("repairs")) {
    navItems.push({ to: "/admin/appointments", icon: CalendarDays, label: "Rendez-vous", badge: getBadge("/admin/appointments", "appointments_today") });
    navItems.push({ to: "/delivery", icon: Truck, label: "Livraisons", badge: getBadge("/delivery", "active_deliveries") });
  }

  const sidebarClasses = isOpen 
    ? "fixed inset-0 z-50 lg:relative lg:inset-auto"
    : "hidden lg:flex";

  return (
    <div className={sidebarClasses}>
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 lg:hidden z-40"
          onClick={onClose}
        />
      )}
      
      <aside className={
        "fixed left-0 top-0 h-screen w-64 bg-card border-r border-border flex flex-col z-50 " +
        "transform transition-transform duration-200 ease-in-out " +
        (isOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0")
      }>
        {/* Logo */}
        <div className="p-4 lg:p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src="/logo.png" alt="AMCPhone" className="w-12 h-12 rounded-md object-contain" />
              <div>
                <h1 className="font-chivo font-bold text-lg text-foreground">AMCPhone</h1>
                <p className="text-xs text-muted-foreground">Martinique</p>
              </div>
            </div>
            <button 
              onClick={onClose}
              className="lg:hidden p-2 hover:bg-secondary rounded-md"
              data-testid="close-menu-btn"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              onClick={() => handleNavClick(item.to)}
              data-testid={"nav-" + item.label.toLowerCase().replace(/\s+/g, "-")}
              className={({ isActive }) =>
                "sidebar-link " + (isActive ? "active" : "")
              }
            >
              <item.icon className="w-5 h-5 shrink-0" />
              <span className="flex-1">{item.label}</span>
              {item.badge > 0 && (
                <Badge count={item.badge} isNew={item.badgeNew} />
              )}
            </NavLink>
          ))}
          
          {/* Admin Section */}
          {isAdmin() && (
            <>
              <div className="pt-4 pb-2">
                <p className="px-4 text-xs font-medium text-muted-foreground uppercase tracking-wider">Administration</p>
              </div>
              <NavLink
                to="/admin/employees"
                onClick={() => handleNavClick(item.to)}
                data-testid="nav-employees"
                className={({ isActive }) => "sidebar-link " + (isActive ? "active" : "")}
              >
                <Shield className="w-5 h-5 shrink-0" />
                <span className="flex-1">Employés</span>
              </NavLink>
              <NavLink
                to="/admin/activity"
                onClick={() => handleNavClick(item.to)}
                data-testid="nav-activity"
                className={({ isActive }) => "sidebar-link " + (isActive ? "active" : "")}
              >
                <Settings className="w-5 h-5 shrink-0" />
                <span className="flex-1">Activité</span>
              </NavLink>
              <NavLink
                to="/orders"
                onClick={() => handleNavClick("/orders")}
                className={({ isActive }) => "sidebar-link " + (isActive ? "active" : "")}
              >
                <ClipboardList className="w-5 h-5 shrink-0" />
                <span className="flex-1">Commandes</span>
              </NavLink>
              <NavLink
                to="/quotes"
                onClick={() => handleNavClick(item.to)}
                className={({ isActive }) => "sidebar-link " + (isActive ? "active" : "")}
              >
                <FileText className="w-5 h-5 shrink-0" />
                <span className="flex-1">Devis</span>
              </NavLink>
              <NavLink
                to="/cash"
                onClick={() => handleNavClick(item.to)}
                className={({ isActive }) => "sidebar-link " + (isActive ? "active" : "")}
              >
                <ShoppingCart className="w-5 h-5 shrink-0" />
                <span className="flex-1">Caisse</span>
              </NavLink>
              <NavLink
                to="/profile"
                onClick={() => handleNavClick(item.to)}
                className={({ isActive }) => "sidebar-link " + (isActive ? "active" : "")}
              >
                <User className="w-5 h-5 shrink-0" />
                <span className="flex-1">Mon Profil</span>
              </NavLink>
              <NavLink
                to="/admin/shop-settings"
                onClick={() => handleNavClick(item.to)}
                data-testid="nav-shop-settings"
                className={({ isActive }) => "sidebar-link " + (isActive ? "active" : "")}
              >
                <Store className="w-5 h-5 shrink-0" />
                <span className="flex-1">Paramètres magasin</span>
              </NavLink>
              <NavLink
                to="/admin/prestations"
                onClick={() => handleNavClick(item.to)}
                data-testid="nav-prestations"
                className={({ isActive }) => "sidebar-link " + (isActive ? "active" : "")}
              >
                <ClipboardList className="w-5 h-5 shrink-0" />
                <span className="flex-1">Prestations</span>
              </NavLink>
              <NavLink
                to="/admin/horaires"
                onClick={() => handleNavClick(item.to)}
                data-testid="nav-horaires"
                className={({ isActive }) => "sidebar-link " + (isActive ? "active" : "")}
              >
                <CalendarDays className="w-5 h-5 shrink-0" />
                <span className="flex-1">Horaires</span>
              </NavLink>
              <NavLink
                to="/admin/intake-requests"
                onClick={() => handleNavClick(item.to)}
                data-testid="nav-intake-requests"
                className={({ isActive }) => "sidebar-link " + (isActive ? "active" : "")}
              >
                <Inbox className="w-5 h-5 shrink-0" />
                <span className="flex-1">Prises en charge</span>
                {stats.pending_intakes > 0 && (
                  <Badge count={stats.pending_intakes} isNew={stats.new_intakes > 0} />
                )}
              </NavLink>
            </>
          )}
        </nav>

        {/* User info & Logout */}
        <div className="p-4 border-t border-border">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-secondary rounded-full flex items-center justify-center">
              <span className="text-sm font-medium">{user && user.name && user.name[0] ? user.name[0].toUpperCase() : "?"}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground truncate">{user && user.name}</p>
              <p className="text-xs text-muted-foreground truncate">
                {user && user.role === "admin" ? "Administrateur" : "Employé"}
              </p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            data-testid="logout-btn"
            className="w-full flex items-center gap-3 px-4 py-2 text-sm text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded-md transition-colors duration-150"
          >
            <LogOut className="w-4 h-4" />
            <span>Déconnexion</span>
          </button>
        </div>
      </aside>
    </div>
  );
};

// Mobile Header Component
const MobileHeader = ({ onMenuClick }) => {
  return (
    <header className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-card border-b border-border flex items-center justify-between px-4 z-40">
      <div className="flex items-center gap-3">
        <img src="/logo.png" alt="AMCPhone" className="w-8 h-8 rounded-md object-contain" />
        <span className="font-chivo font-bold text-foreground">AMCPhone</span>
      </div>
      <div className="flex items-center gap-1">
        <NotificationBell />
        <button 
          onClick={onMenuClick}
          className="p-2 hover:bg-secondary rounded-md"
          data-testid="menu-btn"
        >
          <Menu className="w-6 h-6" />
        </button>
      </div>
    </header>
  );
};

export const MainLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="min-h-screen bg-background noise-bg">
      {/* Mobile Header */}
      <MobileHeader onMenuClick={() => setSidebarOpen(true)} />
      
      {/* Desktop Top Bar */}
      <div className="hidden lg:flex fixed top-0 left-64 right-0 h-14 bg-card/80 backdrop-blur border-b border-border z-30 items-center justify-end px-6">
        <NotificationBell />
      </div>

      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <main className="lg:ml-64 pt-16 lg:pt-14 p-4 lg:p-8">
        {children}
      </main>
    </div>
  );
};
